import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Badge, Button, Card, DeviceLink, Spinner, UserLink } from '@/components';
import { formatAdminDateTime } from '@/lib/format';
import { apiJson } from '@/lib/api';

type FeedbackStatus = 'new' | 'read' | 'archived';

type FeedbackDetail = {
  id: number;
  submitted_at: string;
  message: string;
  status: FeedbackStatus;
  platform: string | null;
  app_version: string | null;
  os_version: string | null;
  locale: string | null;
  installation_id: string;
  account_id: string | null;
  contact_email: string | null;
  admin_notes: string | null;
  read_at: string | null;
  user_email: string | null;
  user_display_name: string | null;
};

export const Route = createFileRoute('/admin/feedback/$id')({
  component: FeedbackDetailPage,
});

function FeedbackDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [notesDraft, setNotesDraft] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'feedback', id],
    queryFn: () => apiJson<FeedbackDetail>(`/v1/admin/feedback/${encodeURIComponent(id)}`),
  });

  const supportResolve = useQuery({
    queryKey: [
      'admin',
      'support',
      'resolve',
      data?.account_id,
      data?.installation_id,
    ],
    enabled: Boolean(data),
    queryFn: () => {
      const params = new URLSearchParams();
      if (data?.account_id) params.set('account_id', data.account_id);
      else if (data?.installation_id) {
        params.set('installation_id', data.installation_id);
      }
      return apiJson<{ id: string }>(
        `/v1/admin/support/resolve?${params.toString()}`,
      );
    },
    retry: false,
  });

  useEffect(() => {
    if (data?.admin_notes != null) {
      setNotesDraft(data.admin_notes);
    } else if (data) {
      setNotesDraft('');
    }
  }, [data?.admin_notes, data?.id]);

  const patch = useMutation({
    mutationFn: (body: { status?: FeedbackStatus; admin_notes?: string }) =>
      apiJson<FeedbackDetail>(`/v1/admin/feedback/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'feedback', id] });
      void qc.invalidateQueries({ queryKey: ['admin', 'feedback'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-red-600">
        {(error as Error).message}
        <button type="button" className="ml-2 underline" onClick={() => void refetch()}>
          Retry
        </button>
      </p>
    );
  }

  if (!data) return null;

  const statusLabel =
    data.status === 'new' ? (
      <Badge variant="primary">New</Badge>
    ) : data.status === 'archived' ? (
      <Badge variant="neutral">Archived</Badge>
    ) : (
      <Badge variant="neutral">Read</Badge>
    );

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">Feedback #{data.id}</h1>
        <div className="flex flex-wrap gap-2">
          {supportResolve.data?.id ? (
            <Link
              to="/admin/support/$id"
              params={{ id: supportResolve.data.id }}
              className="inline-flex items-center rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
            >
              Open support chat
            </Link>
          ) : null}
          <Button variant="secondary" onClick={() => void navigate({ to: '/admin/feedback' })}>
            Back to inbox
          </Button>
        </div>
      </div>

      <Card>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {statusLabel}
          <span className="text-sm text-gray-500">
            Submitted {formatAdminDateTime(data.submitted_at)}
          </span>
          {data.read_at ? (
            <span className="text-sm text-gray-500">
              · Read {formatAdminDateTime(data.read_at)}
            </span>
          ) : null}
        </div>
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-900">{data.message}</p>
      </Card>

      <Card>
        <h2 className="mb-3 text-lg font-medium">Context</h2>
        <dl className="grid gap-2 text-sm sm:grid-cols-2">
          <dt className="text-gray-500">Platform</dt>
          <dd>{data.platform ?? '—'}</dd>
          <dt className="text-gray-500">App version</dt>
          <dd>{data.app_version ?? '—'}</dd>
          <dt className="text-gray-500">OS version</dt>
          <dd>{data.os_version ?? '—'}</dd>
          <dt className="text-gray-500">Locale</dt>
          <dd>{data.locale ?? '—'}</dd>
          <dt className="text-gray-500">Contact email</dt>
          <dd>{data.contact_email ?? '—'}</dd>
          <dt className="text-gray-500">Signed-in user</dt>
          <dd>
            {data.account_id ? (
              <UserLink
                userId={data.account_id}
                displayName={data.user_display_name}
                email={data.user_email}
              >
                {data.user_display_name ?? data.user_email ?? data.account_id}
              </UserLink>
            ) : (
              '—'
            )}
          </dd>
          <dt className="text-gray-500">Device</dt>
          <dd>
            <DeviceLink
              installationId={data.installation_id}
              label="id"
              className="break-all font-mono text-xs"
            />
          </dd>
        </dl>
      </Card>

      <Card>
        <h2 className="mb-3 text-lg font-medium">Admin notes</h2>
        <textarea
          className="min-h-24 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          value={notesDraft}
          onChange={(e) => setNotesDraft(e.target.value)}
          placeholder="Internal notes (not visible to the user)"
        />
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            variant="secondary"
            loading={patch.isPending}
            onClick={() => void patch.mutateAsync({ admin_notes: notesDraft })}
          >
            Save notes
          </Button>
          <Button
            variant="secondary"
            loading={patch.isPending}
            disabled={data.status === 'read'}
            onClick={() => void patch.mutateAsync({ status: 'read' })}
          >
            Mark read
          </Button>
          <Button
            variant="secondary"
            loading={patch.isPending}
            disabled={data.status === 'archived'}
            onClick={() => void patch.mutateAsync({ status: 'archived' })}
          >
            Archive
          </Button>
          <Button
            variant="secondary"
            loading={patch.isPending}
            disabled={data.status === 'new'}
            onClick={() => void patch.mutateAsync({ status: 'new' })}
          >
            Mark as new
          </Button>
        </div>
      </Card>
    </div>
  );
}
